import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Lock, RefreshCw, Mail, Phone, Calendar, Video, MapPin,
  CheckCircle2, XCircle, Undo2, ChevronDown, ChevronUp, Bell, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { listBookings, updateBookingStatus, updateBookingNotes } from "@/lib/api";

const STATUS_TABS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
  { id: "all", label: "All" },
];

const LAST_VISITED_KEY = "admin_last_visited_at";

export default function Admin() {
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("upcoming");
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [lastVisitedAt, setLastVisitedAt] = useState(null);
  const [confirmEmergencyId, setConfirmEmergencyId] = useState(null);

  // On mount, capture the "last visited" timestamp so we can compute "new since"
  useEffect(() => {
    const ts = localStorage.getItem(LAST_VISITED_KEY);
    setLastVisitedAt(ts ? new Date(ts).getTime() : 0);
  }, []);

  const load = async (pwd) => {
    setLoading(true);
    try {
      const data = await listBookings(pwd);
      setBookings(data);
      sessionStorage.setItem("admin_pwd", pwd);
      // After we successfully load, advance the "last visited" timestamp so the badge resets next time
      setTimeout(() => localStorage.setItem(LAST_VISITED_KEY, new Date().toISOString()), 1500);
    } catch (e) {
      toast.error("Incorrect password");
      setBookings(null);
    } finally {
      setLoading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (password.trim()) load(password.trim());
  };

  const setStatus = async (id, status, opts = {}) => {
    setUpdatingId(id);
    try {
      const pwd = sessionStorage.getItem("admin_pwd");
      const updated = await updateBookingStatus(id, status, pwd, opts);
      setBookings((prev) => prev.map((b) => (b.id === id ? updated : b)));
      if (opts.notify_client) {
        toast.success("Booking cancelled. The client has been notified with a rebook link.");
      } else {
        toast.success(`Marked as ${status}.`);
      }
      setConfirmEmergencyId(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update status.");
    } finally {
      setUpdatingId(null);
    }
  };

  const filtered = useMemo(() => {
    if (!bookings) return [];
    if (tab === "all") return bookings;
    if (tab === "upcoming") return bookings.filter((b) => b.status === "confirmed");
    return bookings.filter((b) => b.status === tab);
  }, [bookings, tab]);

  const counts = useMemo(() => {
    if (!bookings) return { upcoming: 0, completed: 0, cancelled: 0, all: 0 };
    return {
      upcoming: bookings.filter((b) => b.status === "confirmed").length,
      completed: bookings.filter((b) => b.status === "completed").length,
      cancelled: bookings.filter((b) => b.status === "cancelled").length,
      all: bookings.length,
    };
  }, [bookings]);

  const newSinceCount = useMemo(() => {
    if (!bookings || lastVisitedAt === null) return 0;
    return bookings.filter((b) => new Date(b.created_at).getTime() > lastVisitedAt).length;
  }, [bookings, lastVisitedAt]);

  const toggleExpand = (id) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-12 pt-10 md:pt-16 pb-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="label-overline">Private · Internal</span>
            <h1 className="font-serif text-5xl text-oxblood mt-3">Bookings</h1>
            <p className="text-inkSoft mt-3">A quiet room. Just you and the consultations on the calendar.</p>
          </div>
          {bookings && (
            <div className="flex items-center gap-3">
              {newSinceCount > 0 && (
                <div className="inline-flex items-center gap-2 bg-gold/15 border border-gold px-4 py-2.5 text-oxblood" data-testid="admin-new-badge">
                  <Bell size={14} className="text-gold" />
                  <span className="text-xs tracking-[0.18em] uppercase font-medium">{newSinceCount} new since last visit</span>
                </div>
              )}
              <button onClick={() => load(sessionStorage.getItem("admin_pwd"))} className="btn-ghost" data-testid="admin-refresh">
                <RefreshCw size={14} /> Refresh
              </button>
            </div>
          )}
        </div>

        {!bookings && (
          <form onSubmit={submit} className="mt-12 max-w-md" data-testid="admin-login-form">
            <label className="label-overline flex items-center gap-2"><Lock size={12} /> Admin password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-flat mt-2"
              placeholder="••••••••••"
              data-testid="admin-password-input"
              autoFocus
            />
            <button type="submit" disabled={loading} className="btn-primary mt-8" data-testid="admin-login-button">
              {loading ? "Verifying…" : "Enter"}
            </button>
            <p className="mt-6 text-xs text-inkMute">
              Notifications also reach your inbox at <code className="font-mono">abourdanne@gmail.com</code> for every booking.
            </p>
          </form>
        )}

        {bookings && (
          <div className="mt-12">
            <div className="grid grid-cols-4 gap-4 mb-8">
              <Stat label="Upcoming" value={counts.upcoming} />
              <Stat label="Completed" value={counts.completed} />
              <Stat label="Cancelled" value={counts.cancelled} />
              <Stat label="Total" value={counts.all} />
            </div>

            <div className="flex items-center gap-1 border-b border-cognac/20 mb-6" data-testid="admin-tabs">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-5 py-3 text-xs tracking-[0.22em] uppercase transition-colors ${
                    tab === t.id ? "text-oxblood border-b-2 border-gold -mb-px" : "text-inkMute hover:text-oxblood"
                  }`}
                  data-testid={`admin-tab-${t.id}`}
                >
                  {t.label} <span className="ml-1 text-cognac">({counts[t.id]})</span>
                </button>
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="border border-cognac/20 p-12 text-center bg-cream/40" data-testid="admin-empty">
                <p className="font-serif text-2xl text-oxblood">Nothing here.</p>
                <p className="text-inkSoft mt-2">No bookings in this view.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="admin-bookings-list">
                {filtered.map((b) => {
                  const isNew = lastVisitedAt !== null && new Date(b.created_at).getTime() > lastVisitedAt;
                  const isExpanded = expandedId === b.id;
                  return (
                    <article
                      key={b.id}
                      className={`border bg-creamAlt/60 transition-all ${
                        b.status === "cancelled" ? "border-cognac/10 opacity-70" : "border-cognac/15"
                      } ${isNew ? "ring-2 ring-gold/40" : ""}`}
                      data-testid={`booking-${b.id}`}
                    >
                      <div className="p-6 grid md:grid-cols-12 gap-6 items-start">
                        <div className="md:col-span-3">
                          <p className="label-overline">When</p>
                          <p className="font-serif text-xl text-oxblood mt-1 flex items-center gap-2">
                            <Calendar size={16} className="text-gold" /> {b.preferred_date}
                          </p>
                          <p className="text-sm text-cognac mt-1">{b.preferred_time}</p>
                          <p className="mt-3 inline-flex items-center gap-1 text-xs text-inkMute">
                            {b.meeting_mode === "virtual" ? <><Video size={12} /> Virtual</> : <><MapPin size={12} /> In person</>}
                          </p>
                          <StatusPill status={b.status} reminderSent={b.reminder_sent} cancelReason={b.cancel_reason} clientNotified={b.client_notified_cancel} />
                          {isNew && (
                            <p className="mt-3 inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase text-gold font-medium">
                              <Bell size={10} /> New
                            </p>
                          )}
                        </div>
                        <div className="md:col-span-3">
                          <p className="label-overline">Client</p>
                          <p className="font-serif text-xl text-oxblood mt-1">{b.full_name}</p>
                          <a href={`mailto:${b.email}`} className="text-sm text-cognac mt-1 flex items-center gap-1 link-underline">
                            <Mail size={12} /> {b.email}
                          </a>
                          {b.phone && (
                            <a href={`tel:${b.phone}`} className="text-sm text-inkSoft mt-1 flex items-center gap-1 link-underline">
                              <Phone size={12} /> {b.phone}
                            </a>
                          )}
                        </div>
                        <div className="md:col-span-3">
                          <p className="label-overline">Topic</p>
                          <p className="text-base text-oxblood mt-1">{b.custom_topic || b.topic}</p>
                          <p className="text-xs text-inkMute mt-2">
                            {[b.household, b.employment, b.living].filter(Boolean).join(" · ")}
                          </p>
                        </div>
                        <div className="md:col-span-3">
                          <p className="label-overline">Situation</p>
                          <p className="text-sm text-inkSoft mt-1 italic line-clamp-3">"{b.situation}"</p>
                          <button
                            type="button"
                            onClick={() => toggleExpand(b.id)}
                            className="mt-3 inline-flex items-center gap-1 text-[11px] tracking-[0.22em] uppercase text-cognac hover:text-oxblood"
                            data-testid={`expand-${b.id}`}
                          >
                            {isExpanded ? <>Hide details <ChevronUp size={12} /></> : <>See full answers <ChevronDown size={12} /></>}
                          </button>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-gold/30 bg-cream/50 px-6 py-6 space-y-6" data-testid={`booking-details-${b.id}`}>
                          <div className="grid md:grid-cols-2 gap-x-10 gap-y-4">
                            <DetailRow label="Full situation" value={b.situation} />
                            <DetailRow label="Household" value={b.household || "—"} />
                            <DetailRow label="Employment" value={b.employment || "—"} />
                            <DetailRow label="Living" value={b.living || "—"} />
                            <DetailRow label="In school" value={b.in_school || "—"} />
                            <DetailRow label="Dependents" value={b.dependents || "—"} />
                            <DetailRow label="Debt level" value={b.debt_level || "—"} />
                            <DetailRow label="Confidence" value={b.confidence || "—"} />
                            <DetailRow label="Timeline" value={b.timeline || "—"} />
                            {b.custom_topic && <DetailRow label="Custom goal" value={b.custom_topic} />}
                            <DetailRow label="Created" value={new Date(b.created_at).toLocaleString()} />
                            <DetailRow label="Booking #" value={b.id.slice(0, 8)} />
                          </div>
                          <NotesEditor
                            booking={b}
                            onSaved={(updated) => setBookings((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
                          />
                        </div>
                      )}

                      <div className="border-t border-cognac/10 px-6 py-4 flex flex-wrap items-center gap-2 bg-creamAlt/40">
                        {b.status === "confirmed" && (
                          <>
                            <button
                              type="button"
                              disabled={updatingId === b.id}
                              onClick={() => setStatus(b.id, "completed")}
                              className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase border border-gold/60 text-oxblood px-3 py-2 hover:bg-gold/10 transition-colors disabled:opacity-50"
                              data-testid={`mark-completed-${b.id}`}
                            >
                              <CheckCircle2 size={12} /> Mark done
                            </button>
                            {confirmEmergencyId !== b.id ? (
                              <button
                                type="button"
                                disabled={updatingId === b.id}
                                onClick={() => setConfirmEmergencyId(b.id)}
                                className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase border border-terracotta/70 text-terracotta px-3 py-2 hover:bg-terracotta/10 transition-colors disabled:opacity-50"
                                data-testid={`emergency-cancel-${b.id}`}
                              >
                                <AlertTriangle size={12} /> Emergency cancel & notify
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase border border-terracotta/40 px-2 py-1 bg-terracotta/5 text-terracotta">
                                Cancel & email {b.full_name.split(" ")[0]}?
                                <button
                                  type="button"
                                  disabled={updatingId === b.id}
                                  onClick={() => setStatus(b.id, "cancelled", { notify_client: true, reason: "advisor_emergency" })}
                                  className="ml-1 underline font-semibold"
                                  data-testid={`confirm-emergency-${b.id}`}
                                >
                                  Yes
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setConfirmEmergencyId(null)}
                                  className="ml-1 text-inkMute"
                                >
                                  No
                                </button>
                              </span>
                            )}
                            <button
                              type="button"
                              disabled={updatingId === b.id}
                              onClick={() => setStatus(b.id, "cancelled")}
                              className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase border border-cognac/40 text-cognac px-3 py-2 hover:bg-cognac/10 transition-colors disabled:opacity-50"
                              data-testid={`mark-cancelled-${b.id}`}
                              title="Cancel without emailing the client (silent)"
                            >
                              <XCircle size={12} /> Cancel quietly
                            </button>
                          </>
                        )}
                        {b.status !== "confirmed" && (
                          <button
                            type="button"
                            disabled={updatingId === b.id}
                            onClick={() => setStatus(b.id, "confirmed")}
                            className="inline-flex items-center gap-1 text-[11px] tracking-[0.18em] uppercase border border-cognac/40 text-cognac px-3 py-2 hover:bg-cognac/10 transition-colors disabled:opacity-50"
                            data-testid={`mark-restore-${b.id}`}
                          >
                            <Undo2 size={12} /> Restore
                          </button>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="mt-16">
          <Link to="/" className="text-sm text-cognac link-underline" data-testid="admin-home-link">← Back to site</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-gold/30 bg-cream/60 p-5">
      <p className="label-overline">{label}</p>
      <p className="font-serif text-4xl text-oxblood mt-2">{value}</p>
    </div>
  );
}

function StatusPill({ status, reminderSent, cancelReason, clientNotified }) {
  const map = {
    confirmed: { label: "Confirmed", cls: "border-gold/60 text-oxblood bg-gold/10" },
    completed: { label: "Completed", cls: "border-cognac/40 text-cognac bg-cream" },
    cancelled: { label: "Cancelled", cls: "border-cognac/20 text-inkMute bg-cream/40" },
  };
  const m = map[status] || map.confirmed;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      <span className={`inline-flex items-center text-[10px] tracking-[0.22em] uppercase border px-2.5 py-1 ${m.cls}`}>
        {m.label}
      </span>
      {reminderSent && (
        <span className="inline-flex items-center text-[10px] tracking-[0.22em] uppercase border border-cognac/20 text-inkMute px-2.5 py-1">
          Reminder sent
        </span>
      )}
      {status === "cancelled" && cancelReason === "advisor_emergency" && (
        <span className="inline-flex items-center text-[10px] tracking-[0.22em] uppercase border border-terracotta/40 text-terracotta px-2.5 py-1">
          Emergency
        </span>
      )}
      {status === "cancelled" && clientNotified && (
        <span className="inline-flex items-center text-[10px] tracking-[0.22em] uppercase border border-gold/40 text-cognac px-2.5 py-1">
          Client notified
        </span>
      )}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="label-overline">{label}</p>
      <p className="text-sm text-oxblood mt-1 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function NotesEditor({ booking, onSaved }) {
  const [draft, setDraft] = useState({
    private_notes: booking.private_notes || "",
    meeting_summary: booking.meeting_summary || "",
    meeting_duration_minutes: booking.meeting_duration_minutes || "",
    things_achieved: booking.things_achieved || "",
    goals_for_next_meeting: booking.goals_for_next_meeting || "",
    shared_notes: booking.shared_notes || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const pwd = sessionStorage.getItem("admin_pwd");
      const payload = {
        ...draft,
        meeting_duration_minutes: draft.meeting_duration_minutes === ""
          ? null
          : Number(draft.meeting_duration_minutes),
      };
      const updated = await updateBookingNotes(booking.id, payload, pwd);
      onSaved(updated);
      toast.success("Notes saved.");
    } catch (e) {
      toast.error("Could not save notes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-cognac/15 bg-creamAlt/40 p-5 space-y-5" data-testid={`notes-editor-${booking.id}`}>
      <div>
        <p className="label-overline flex items-center gap-2">
          <Lock size={11} /> Private notes — visible to you only
        </p>
        <textarea
          rows={3}
          value={draft.private_notes}
          onChange={(e) => setDraft({ ...draft, private_notes: e.target.value })}
          className="input-flat mt-2 resize-none border-b"
          placeholder="Anything you want to remember — only you can see this."
          data-testid={`note-private-${booking.id}`}
        />
      </div>

      <div className="border-t border-gold/30 pt-5">
        <p className="label-overline text-gold">Meeting record — visible to the client in their profile</p>
        <div className="grid md:grid-cols-3 gap-x-6 gap-y-4 mt-3">
          <div className="md:col-span-2">
            <label className="text-[10px] tracking-[0.22em] uppercase text-inkMute">Recap of the meeting</label>
            <textarea
              rows={3}
              value={draft.meeting_summary}
              onChange={(e) => setDraft({ ...draft, meeting_summary: e.target.value })}
              className="input-flat mt-1 resize-none border-b"
              placeholder="What was covered in this meeting"
              data-testid={`note-summary-${booking.id}`}
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-inkMute">Duration (minutes)</label>
            <input
              type="number"
              min="0"
              value={draft.meeting_duration_minutes}
              onChange={(e) => setDraft({ ...draft, meeting_duration_minutes: e.target.value })}
              className="input-flat mt-1 border-b"
              placeholder="60"
              data-testid={`note-duration-${booking.id}`}
            />
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-x-6 gap-y-4 mt-4">
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-inkMute">Things we achieved</label>
            <textarea
              rows={3}
              value={draft.things_achieved}
              onChange={(e) => setDraft({ ...draft, things_achieved: e.target.value })}
              className="input-flat mt-1 resize-none border-b"
              placeholder="Wins from today"
              data-testid={`note-achieved-${booking.id}`}
            />
          </div>
          <div>
            <label className="text-[10px] tracking-[0.22em] uppercase text-inkMute">Goals for next meeting</label>
            <textarea
              rows={3}
              value={draft.goals_for_next_meeting}
              onChange={(e) => setDraft({ ...draft, goals_for_next_meeting: e.target.value })}
              className="input-flat mt-1 resize-none border-b"
              placeholder="What to focus on before we meet again"
              data-testid={`note-next-${booking.id}`}
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-[10px] tracking-[0.22em] uppercase text-inkMute">Extra notes for the client</label>
          <textarea
            rows={2}
            value={draft.shared_notes}
            onChange={(e) => setDraft({ ...draft, shared_notes: e.target.value })}
            className="input-flat mt-1 resize-none border-b"
            placeholder="Any other reflections, encouragements, links…"
            data-testid={`note-shared-${booking.id}`}
          />
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          onClick={save}
          disabled={saving}
          className="btn-primary disabled:opacity-50"
          data-testid={`save-notes-${booking.id}`}
        >
          {saving ? "Saving…" : "Save notes"}
        </button>
      </div>
    </div>
  );
}
